import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Bars3Icon,
  CalendarIcon,
  CheckCircleIcon,
  FunnelIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  RocketLaunchIcon,
  TrashIcon,
  UserCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown";
import { OKRFormModal } from "../components/okr/OKRFormModal";
import { OKRScoreDisplay } from "../components/okr/OKRScoreDisplay";
import { useOrganization } from "../context/OrganizationContext";
import { useSessionFilter } from "../hooks/useFilterPersistence";
import {
  OKRCreate,
  OKRFilters,
  okrService,
  OKRUpdate,
  OrganizationOKR,
} from "../services/okrs";
import {
  planningConfigApi,
  PlanningConfiguration,
  PlanningCycle,
  planningCyclesApi,
} from "../services/planningCycles";
import { formatCommitmentType } from "../utils/enumFormatting";

const ObjectivesV2: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrg, userRole } = useOrganization();

  // State
  const [okrs, setOkrs] = useState<OrganizationOKR[]>([]);
  const [allOkrs, setAllOkrs] = useState<OrganizationOKR[]>([]); // Unfiltered OKRs for dropdowns
  const [planningCycles, setPlanningCycles] = useState<PlanningCycle[]>([]);
  const [planningConfig, setPlanningConfig] =
    useState<PlanningConfiguration | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters - Using persistent session storage with multi-select support
  const [selectedCycleTypes, setSelectedCycleTypes] = useSessionFilter<
    string[]
  >(
    "selectedCycleTypes",
    [], // Default: all cycle types
    "objectives",
  );
  const [selectedSpecificCycles, setSelectedSpecificCycles] = useSessionFilter<
    string[]
  >("selectedSpecificCycles", [], "objectives");
  const [selectedOwners, setSelectedOwners] = useSessionFilter<string[]>(
    "selectedOwners",
    [], // Default: all owners
    "objectives",
  );
  const [searchInput, setSearchInput] = useSessionFilter<string>(
    "searchInput",
    "",
    "objectives",
  );
  const [statusFilters, setStatusFilters] = useSessionFilter<string[]>(
    "statusFilters",
    ["active", "draft"], // Default: Active and Draft
    "objectives",
  );
  const [typeFilters, setTypeFilters] = useSessionFilter<string[]>(
    "typeFilters",
    [], // Default: all types
    "objectives",
  );
  const [hierarchyFilters, setHierarchyFilters] = useSessionFilter<string[]>(
    "hierarchyFilters",
    [], // Default: empty array means all hierarchies
    "objectives",
  );
  const [selectedParentIds, setSelectedParentIds] = useSessionFilter<string[]>(
    "selectedParentIds",
    [], // Default: empty array means all parents
    "objectives",
  );

  // Debounce timer reference
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);

  // UI State
  const [showOkrForm, setShowOkrForm] = useState(false);
  const [showPlanningConfig, setShowPlanningConfig] = useState(false);
  const [showStrategicNarrative, setShowStrategicNarrative] = useState(false);
  const [initialParentId, setInitialParentId] = useState<string | null>(null);
  const [initialRelatedKrId, setInitialRelatedKrId] = useState<string | null>(
    null,
  );
  const [initialKrDetails, setInitialKrDetails] = useState<{
    description: string;
    currentUserName: string;
  } | null>(null);

  // Drag and drop state
  const [reorderMode, setReorderMode] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [originalOrder, setOriginalOrder] = useState<OrganizationOKR[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Permissions
  const canEdit = userRole === "admin" || userRole === "editor";
  const canViewAll =
    userRole === "admin" || userRole === "editor" || userRole === "viewer";
  const isAdmin = userRole === "admin";

  // Validate and fix invalid stored filter values
  useEffect(() => {
    // Ensure arrays are arrays
    if (!Array.isArray(statusFilters)) {
      setStatusFilters(["active", "draft"]);
    }
    if (!Array.isArray(typeFilters)) {
      setTypeFilters([]);
    }
    if (!Array.isArray(selectedOwners)) {
      setSelectedOwners([]);
    }
    if (!Array.isArray(selectedCycleTypes)) {
      setSelectedCycleTypes([]);
    }
    if (!Array.isArray(hierarchyFilters)) {
      setHierarchyFilters([]);
    }
    if (!Array.isArray(selectedParentIds)) {
      setSelectedParentIds([]);
    }
  }, [
    statusFilters,
    typeFilters,
    selectedOwners,
    selectedCycleTypes,
    hierarchyFilters,
    selectedParentIds,
    setStatusFilters,
    setTypeFilters,
    setSelectedOwners,
    setSelectedCycleTypes,
    setHierarchyFilters,
    setSelectedParentIds,
  ]);

  // Helper functions for planning cycle display
  const getCycleTypeIcon = (cycleType: string) => {
    switch (cycleType) {
      case "3-year":
        return "🎯";
      case "year":
        return "📅";
      case "quarter":
        return "📊";
      case "30-day":
        return "📆";
      default:
        return "📋";
    }
  };

  const getCycleTypeLabel = (cycleType: string) => {
    switch (cycleType) {
      case "3-year":
        return "3-Year Strategic";
      case "year":
        return "Annual";
      case "quarter":
        return "Quarterly";
      case "30-day":
        return "Monthly";
      default:
        return cycleType;
    }
  };

  // Clear all filters function
  const clearAllFilters = useCallback(() => {
    setSelectedCycleTypes([]);
    setSelectedSpecificCycles([]);
    setSelectedOwners([]);
    setSearchInput("");
    setStatusFilters(["active", "draft"]);
    setTypeFilters([]);
    setHierarchyFilters([]);
    setSelectedParentIds([]);
  }, [
    setSelectedCycleTypes,
    setSelectedSpecificCycles,
    setSelectedOwners,
    setSearchInput,
    setStatusFilters,
    setTypeFilters,
    setHierarchyFilters,
    setSelectedParentIds,
  ]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      selectedCycleTypes.length > 0 ||
      selectedSpecificCycles.length > 0 ||
      selectedOwners.length > 0 ||
      searchInput !== "" ||
      statusFilters.length !== 2 ||
      !statusFilters.includes("active") ||
      !statusFilters.includes("draft") ||
      typeFilters.length > 0 ||
      hierarchyFilters.length > 0 ||
      selectedParentIds.length > 0
    );
  }, [
    selectedCycleTypes,
    selectedSpecificCycles,
    selectedOwners,
    searchInput,
    statusFilters,
    typeFilters,
    hierarchyFilters,
    selectedParentIds,
  ]);

  // Check URL params for creating new OKR
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("create") === "true") {
      const parentId = params.get("parent_id");
      const relatedKrId = params.get("related_kr");
      const krDesc = params.get("kr_desc");
      const currentUser = params.get("current_user");

      setInitialParentId(parentId);
      setInitialRelatedKrId(relatedKrId);

      // Capture KR details for auto-naming if available
      if (krDesc) {
        setInitialKrDetails({
          description: decodeURIComponent(krDesc),
          currentUserName: currentUser ? decodeURIComponent(currentUser) : "",
        });
      } else {
        setInitialKrDetails(null);
      }

      setShowOkrForm(true);

      // Clear the URL params after opening modal
      navigate("/okrs", { replace: true });
    }
  }, [location.search, navigate]);

  // Store filter values in a ref to access them without causing re-renders
  const filtersRef = useRef({
    selectedCycleTypes,
    selectedSpecificCycles,
    selectedOwners,
    statusFilters,
    typeFilters,
    hierarchyFilters,
    selectedParentIds,
  });

  // Update the ref when filters change
  useEffect(() => {
    filtersRef.current = {
      selectedCycleTypes,
      selectedSpecificCycles,
      selectedOwners,
      statusFilters,
      typeFilters,
      hierarchyFilters,
      selectedParentIds,
    };
  }, [
    selectedCycleTypes,
    selectedSpecificCycles,
    selectedOwners,
    statusFilters,
    typeFilters,
    hierarchyFilters,
    selectedParentIds,
  ]);

  // Fetch data function
  const fetchData = useCallback(
    async (searchTerm?: string) => {
      if (!selectedOrg) return;

      try {
        // Only show loading on initial load, not on search updates
        if (searchTerm === undefined) {
          setLoading(true);
        }
        setError(null);

        // Fetch planning configuration and cycles
        const [configData, cyclesData] = await Promise.all([
          planningConfigApi.getConfig(selectedOrg.id),
          planningCyclesApi.listCycles(selectedOrg.id),
        ]);

        setPlanningConfig(configData);
        setPlanningCycles(cyclesData);

        // Build filters using ref values for stable access
        const filters: OKRFilters = {
          include_children: true,
          include_relationships: true,
        };

        const currentFilters = filtersRef.current;

        // Since backend doesn't support multi-select filters yet,
        // we'll fetch all and filter client-side
        // When no filters are selected, include orphaned OKRs
        if (
          currentFilters.selectedCycleTypes.length === 0 &&
          currentFilters.selectedSpecificCycles.length === 0
        ) {
          filters.include_orphaned = true;
        }

        // Use the passed searchTerm
        if (searchTerm) filters.search = searchTerm;

        // Fetch OKRs
        const okrsData = await okrService.listOKRs(selectedOrg.id, filters);

        // Store unfiltered OKRs for dropdowns
        setAllOkrs(okrsData);

        // Enhanced sorting logic:
        // 1. Corporate objectives first
        // 2. By planning cycle type (3-year → annual → quarterly → monthly → none)
        // 3. Within same type, by cycle start date
        // 4. OKRs with children before those without
        // 5. By creation date as final tiebreaker
        const cycleTypeOrder: { [key: string]: number } = {
          "3-year": 1,
          year: 2,
          quarter: 3,
          "30-day": 4,
        };

        // Apply client-side filtering for multi-select features not supported by backend
        let filteredOkrs = okrsData;

        // Filter by cycle types if backend doesn't support multi-cycle filtering
        if (
          currentFilters.selectedCycleTypes.length > 0 ||
          currentFilters.selectedSpecificCycles.length > 0
        ) {
          filteredOkrs = filteredOkrs.filter((okr) => {
            const cycle = cyclesData.find(
              (c) => c.id === okr.planning_cycle_id,
            );
            if (!cycle)
              return (
                currentFilters.selectedCycleTypes.length === 0 &&
                currentFilters.selectedSpecificCycles.length === 0
              );

            // If specific cycles are selected, they take priority (AND with cycle type)
            if (currentFilters.selectedSpecificCycles.length > 0) {
              const matchesSpecificCycle = currentFilters.selectedSpecificCycles.includes(cycle.id);
              // If cycle types are also selected, must match both
              if (currentFilters.selectedCycleTypes.length > 0) {
                return matchesSpecificCycle && currentFilters.selectedCycleTypes.includes(cycle.cycle_type);
              }
              // Only specific cycles selected
              return matchesSpecificCycle;
            }

            // Only cycle types selected (no specific cycles)
            return currentFilters.selectedCycleTypes.includes(cycle.cycle_type);
          });
        }

        // Filter by owners if backend doesn't support multi-owner filtering
        if (currentFilters.selectedOwners.length > 0) {
          filteredOkrs = filteredOkrs.filter(
            (okr) =>
              okr.owner_id &&
              currentFilters.selectedOwners.includes(okr.owner_id),
          );
        }

        // Filter by status if backend doesn't support multi-status filtering
        if (
          currentFilters.statusFilters.length > 0 &&
          currentFilters.statusFilters.length < 4
        ) {
          filteredOkrs = filteredOkrs.filter((okr) =>
            currentFilters.statusFilters.includes(okr.status),
          );
        }

        // Filter by type if backend doesn't support multi-type filtering
        if (currentFilters.typeFilters.length > 0) {
          filteredOkrs = filteredOkrs.filter((okr) =>
            currentFilters.typeFilters.includes(
              okr.commitment_type || "committed",
            ),
          );
        }

        // Apply hierarchy filters (multi-select checkboxes)
        if (currentFilters.hierarchyFilters.length > 0) {
          filteredOkrs = filteredOkrs.filter((okr) => {
            const checks = [];

            // Check each selected hierarchy type
            if (currentFilters.hierarchyFilters.includes("top-level")) {
              checks.push(!okr.parent_okr_id);
            }
            if (currentFilters.hierarchyFilters.includes("has-children")) {
              checks.push(okr.child_okrs && okr.child_okrs.length > 0);
            }
            if (currentFilters.hierarchyFilters.includes("child-only")) {
              checks.push(!!okr.parent_okr_id);
            }
            if (currentFilters.hierarchyFilters.includes("corporate")) {
              checks.push(okr.is_corporate);
            }

            // Include OKR if it matches ANY of the selected hierarchy types (OR logic)
            return checks.some((check) => check);
          });
        }

        // Apply specific parent filter - show each selected parent and its children
        if (currentFilters.selectedParentIds.length > 0) {
          filteredOkrs = filteredOkrs.filter((okr) => {
            // Include if OKR is one of the selected parents
            if (currentFilters.selectedParentIds.includes(okr.id)) {
              return true;
            }
            // Include if OKR is a child of any selected parent
            if (
              okr.parent_okr_id &&
              currentFilters.selectedParentIds.includes(okr.parent_okr_id)
            ) {
              return true;
            }
            return false;
          });
        }

        const sortedOkrs = filteredOkrs.sort((a, b) => {
          // Corporate first
          if (a.is_corporate !== b.is_corporate) {
            return a.is_corporate ? -1 : 1;
          }

          // Get planning cycles for comparison
          const cycleA = cyclesData.find((c) => c.id === a.planning_cycle_id);
          const cycleB = cyclesData.find((c) => c.id === b.planning_cycle_id);

          // By cycle type
          const typeA = cycleA?.cycle_type || "none";
          const typeB = cycleB?.cycle_type || "none";
          const orderA = cycleTypeOrder[typeA] || 999;
          const orderB = cycleTypeOrder[typeB] || 999;

          if (orderA !== orderB) {
            return orderA - orderB;
          }

          // Within same type, by start date
          if (cycleA && cycleB && cycleA.start_date !== cycleB.start_date) {
            return (
              new Date(cycleA.start_date).getTime() -
              new Date(cycleB.start_date).getTime()
            );
          }

          // OKRs with children first
          const hasChildrenA = a.child_okrs && a.child_okrs.length > 0;
          const hasChildrenB = b.child_okrs && b.child_okrs.length > 0;
          if (hasChildrenA !== hasChildrenB) {
            return hasChildrenA ? -1 : 1;
          }

          // By creation date as final tiebreaker
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        });

        setOkrs(sortedOkrs);
      } catch (err: any) {
        setError(err.response?.data?.detail || "Failed to fetch objectives");
        console.error("Error fetching objectives:", err);
      } finally {
        setLoading(false);
      }
    },
    [selectedOrg],
  );

  // Fetch data on mount and when non-search filters change
  useEffect(() => {
    if (!selectedOrg) return;
    fetchData();
  }, [
    selectedOrg,
    selectedCycleTypes,
    selectedSpecificCycles,
    selectedOwners,
    statusFilters,
    typeFilters,
    hierarchyFilters,
    selectedParentIds,
    fetchData,
  ]);

  // Handle search separately with debouncing
  useEffect(() => {
    if (!selectedOrg) return;

    // Clear existing timer
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    // Set new timer
    searchTimerRef.current = setTimeout(() => {
      fetchData(searchInput);
    }, 300); // 300ms delay

    // Cleanup
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [searchInput, selectedOrg, fetchData]);

  // Group planning cycles by type for smart selection
  const groupedCycles = useMemo(() => {
    const groups: Record<string, PlanningCycle[]> = {
      "3-year": [],
      year: [],
      quarter: [],
      "30-day": [],
    };

    planningCycles.forEach((cycle) => {
      if (groups[cycle.cycle_type]) {
        groups[cycle.cycle_type].push(cycle);
      }
    });

    return groups;
  }, [planningCycles]);

  // Get current cycle of each type
  const currentCyclesByType = useMemo(() => {
    const current: Record<string, PlanningCycle | null> = {
      "3-year": null,
      year: null,
      quarter: null,
      "30-day": null,
    };

    Object.entries(groupedCycles).forEach(([type, cycles]) => {
      const activeCycle = cycles.find((c) => c.is_active);
      if (activeCycle) {
        current[type] = activeCycle;
      } else if (cycles.length > 0) {
        // Find the cycle that includes today's date
        const today = new Date();
        const currentCycle = cycles.find((c) => {
          const start = new Date(c.start_date);
          const end = new Date(c.end_date);
          return today >= start && today <= end;
        });
        if (currentCycle) {
          current[type] = currentCycle;
        }
      }
    });

    return current;
  }, [groupedCycles]);

  // Calculate statistics for all OKRs
  const statistics = useMemo(() => {
    const stats = {
      total: okrs.length,
      corporate: okrs.filter((o) => o.is_corporate).length,
      active: okrs.filter((o) => o.status === "active").length,
      completed: okrs.filter((o) => o.status === "completed").length,
      avgProgress:
        okrs.length > 0
          ? Math.round(
              okrs.reduce((sum, o) => sum + (o.progress_percentage || 0), 0) /
                okrs.length,
            )
          : 0,
      byOwner: new Map<string, number>(),
    };

    okrs.forEach((okr) => {
      if (okr.owner) {
        const ownerName =
          `${okr.owner.first_name || ""} ${okr.owner.last_name || ""}`.trim() ||
          okr.owner.email;
        stats.byOwner.set(ownerName, (stats.byOwner.get(ownerName) || 0) + 1);
      }
    });

    return stats;
  }, [okrs]);

  // Get unique owners for filter dropdown
  const uniqueOwners = useMemo(() => {
    const owners = new Map<string, { id: string; name: string }>();

    allOkrs.forEach((okr) => {
      // Use allOkrs to get all possible owners
      if (okr.owner) {
        const name =
          `${okr.owner.first_name || ""} ${okr.owner.last_name || ""}`.trim() ||
          okr.owner.email;
        owners.set(okr.owner.id, { id: okr.owner.id, name });
      }
    });
    return Array.from(owners.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [allOkrs]);

  // Get OKRs that have children for parent filter dropdown (use unfiltered list)
  const parentOkrs = useMemo(() => {
    return allOkrs
      .filter((o) => o.child_okrs && o.child_okrs.length > 0)
      .sort((a, b) => a.objective.localeCompare(b.objective));
  }, [allOkrs]);

  // Get available specific cycles based on selected cycle types
  const availableSpecificCycles = useMemo(() => {
    if (selectedCycleTypes.length === 0) {
      return [];
    }

    // Get cycles that match selected types and have OKRs
    const cycles = planningCycles.filter(
      (cycle) =>
        selectedCycleTypes.includes(cycle.cycle_type) &&
        allOkrs.some((okr) => okr.planning_cycle_id === cycle.id),
    );

    // Group by type for better organization
    const grouped: Record<string, PlanningCycle[]> = {};
    selectedCycleTypes.forEach((type: string) => {
      grouped[type] = cycles
        .filter((c) => c.cycle_type === type)
        .sort(
          (a, b) =>
            new Date(b.start_date).getTime() - new Date(a.start_date).getTime(),
        );
    });

    return grouped;
  }, [selectedCycleTypes, planningCycles, allOkrs]);

  // Ordered OKRs for drag and drop
  const orderedOkrs = useMemo(() => {
    return [...okrs].sort((a, b) => {
      // Handle null/undefined order_index by treating them as MAX value
      // This ensures they appear at the end, not the beginning
      const aIndex = a.order_index ?? Number.MAX_SAFE_INTEGER;
      const bIndex = b.order_index ?? Number.MAX_SAFE_INTEGER;

      if (aIndex !== bIndex) {
        return aIndex - bIndex;
      }
      // Fallback to creation date (oldest first) for stable sorting
      const aDate = new Date(a.created_at).getTime();
      const bDate = new Date(b.created_at).getTime();
      return aDate - bDate;
    });
  }, [okrs]);

  // Sortable OKR Card Component
  const SortableOKRCard: React.FC<{ okr: OrganizationOKR }> = ({ okr }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: okr.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const hasChildren = okr.child_okrs && okr.child_okrs.length > 0;

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`bg-white rounded-lg border border-gray-200 p-4 mb-4 ${isDragging ? "opacity-50" : ""}`}
      >
        {/* Header with title and actions */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <div className="flex items-start">
              <button
                {...attributes}
                {...listeners}
                className="text-gray-400 hover:text-gray-600 p-1 cursor-grab active:cursor-grabbing mr-2 flex-shrink-0"
                title="Drag to reorder"
              >
                <Bars3Icon className="h-4 w-4" />
              </button>
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 text-sm">
                  {okr.formatted_reference && (
                    <span className="text-gray-500 text-xs mr-2">
                      {okr.formatted_reference}
                    </span>
                  )}
                  {okr.parent_okr_id && (
                    <span
                      className="text-gray-400 mr-1"
                      title="Has parent objective"
                    >
                      ↳
                    </span>
                  )}
                  {okr.objective}
                </h3>
                {okr.is_corporate && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 mt-1">
                    Corporate
                  </span>
                )}
                {okr.description && (
                  <p className="text-xs text-gray-500 mt-1">
                    {okr.description}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2 ml-2">
            {canEdit && (
              <>
                <button
                  onClick={() => navigate(`/okrs/${okr.id}`)}
                  className="text-blue-600 hover:text-blue-900"
                  title="Edit"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteOkr(okr.id)}
                  className="text-red-600 hover:text-red-900"
                  title="Delete"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Owner and Status */}
        <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
          <div className="flex items-center text-gray-600">
            <UserCircleIcon className="h-4 w-4 mr-1" />
            <span className="truncate">
              {okr.owner
                ? okr.owner.first_name || okr.owner.last_name
                  ? `${okr.owner.first_name || ""} ${okr.owner.last_name || ""}`.trim()
                  : okr.owner.email
                : "Unassigned"}
            </span>
          </div>
          <div className="flex justify-end">
            {renderStatusBadge(okr.status)}
          </div>
        </div>

        {/* Type and Score */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <span
              className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                okr.commitment_type === "aspirational"
                  ? "bg-purple-100 text-purple-800"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {formatCommitmentType(okr.commitment_type)}
            </span>
          </div>
          <div className="flex justify-end">
            <OKRScoreDisplay
              progress={okr.progress_percentage || 0}
              score={okr.score}
            />
          </div>
        </div>

        {/* Metrics */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center space-x-4 text-xs">
            {/* Key Results */}
            <button
              onClick={() => navigate(`/okrs/${okr.id}`)}
              className="flex items-center space-x-1 text-gray-600 hover:text-blue-600"
            >
              <CheckCircleIcon className="h-4 w-4 text-green-500" />
              <span className="font-medium">{okr.key_results.length} KRs</span>
            </button>

            {/* Child OKRs */}
            {hasChildren && (
              <div className="flex items-center space-x-1 text-gray-600">
                <RocketLaunchIcon className="h-4 w-4 text-purple-500" />
                <span className="font-medium">
                  {okr.child_okrs?.length} Child
                  {okr.child_okrs?.length === 1 ? "" : "ren"}
                </span>
              </div>
            )}

            {/* Relationships */}
            {okr.relationships && okr.relationships.length > 0 && (
              <button
                onClick={() => navigate(`/okrs/${okr.id}`)}
                className="flex items-center space-x-1 text-gray-600 hover:text-blue-600"
              >
                <LinkIcon className="h-4 w-4 text-blue-500" />
                <span className="font-medium">{okr.relationships?.length}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Sortable OKR Row Component
  const SortableOKRRow: React.FC<{ okr: OrganizationOKR }> = ({ okr }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: okr.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const hasChildren = okr.child_okrs && okr.child_okrs.length > 0;

    return (
      <tr
        ref={setNodeRef}
        style={style}
        className={`hover:bg-gray-50 ${isDragging ? "opacity-50" : ""}`}
      >
        <td className="px-4 py-3 text-sm w-2/5 max-w-[300px]">
          <div className="flex items-center">
            <button
              {...attributes}
              {...listeners}
              className="text-gray-400 hover:text-gray-600 p-1 cursor-grab active:cursor-grabbing mr-2 flex-shrink-0"
              title="Drag to reorder"
            >
              <Bars3Icon className="h-4 w-4" />
            </button>
            <div className="flex-1 min-w-0">
              <button
                onClick={() => navigate(`/okrs/${okr.id}`)}
                className="text-left hover:text-blue-600 font-medium text-gray-900 block"
                title={okr.objective}
              >
                <span className="line-clamp-2">
                  {okr.formatted_reference && (
                    <span className="text-gray-500 text-xs mr-2">
                      {okr.formatted_reference}
                    </span>
                  )}
                  {okr.parent_okr_id && (
                    <span
                      className="text-gray-400 mr-1"
                      title="Has parent objective"
                    >
                      ↳
                    </span>
                  )}
                  {okr.objective}
                </span>
              </button>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {/* Planning Cycle Indicator */}
                {(() => {
                  const cycle = planningCycles.find(
                    (c) => c.id === okr.planning_cycle_id,
                  );
                  if (cycle) {
                    return (
                      <span className="inline-flex items-center text-xs text-gray-600">
                        {getCycleTypeIcon(cycle.cycle_type)} {cycle.name}
                      </span>
                    );
                  }
                  return null;
                })()}

                {okr.is_corporate && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                    ⭐ Corporate
                  </span>
                )}
              </div>
              {okr.description && (
                <p
                  className="text-xs text-gray-500 line-clamp-1 mt-1"
                  title={okr.description}
                >
                  {okr.description}
                </p>
              )}
            </div>
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-gray-500 w-1/6">
          {okr.owner ? (
            <div className="flex items-center">
              <UserCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
              <span className="truncate">
                {okr.owner.first_name || okr.owner.last_name
                  ? `${okr.owner.first_name || ""} ${okr.owner.last_name || ""}`.trim()
                  : okr.owner.email}
              </span>
            </div>
          ) : (
            <span className="text-gray-400">Unassigned</span>
          )}
        </td>
        <td className="px-4 py-3 text-sm w-1/8">
          <span
            className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
              okr.commitment_type === "aspirational"
                ? "bg-purple-100 text-purple-800"
                : "bg-blue-100 text-blue-800"
            }`}
          >
            {formatCommitmentType(okr.commitment_type)}
          </span>
        </td>
        <td className="px-4 py-3 text-sm w-1/8">
          <OKRScoreDisplay
            progress={okr.progress_percentage || 0}
            score={okr.score}
          />
        </td>
        <td className="px-4 py-3 text-sm w-1/8">
          {renderStatusBadge(okr.status)}
        </td>
        <td className="px-4 py-3 text-sm w-1/8">
          <div className="flex items-center space-x-3">
            {/* Key Results */}
            <button
              onClick={() => navigate(`/okrs/${okr.id}`)}
              className="group flex items-center space-x-1 hover:text-blue-600 transition-colors"
              title="View Key Results"
            >
              <CheckCircleIcon className="h-4 w-4 text-green-500" />
              <span className="font-medium text-gray-700 group-hover:text-blue-600">
                {okr.key_results.length}
              </span>
            </button>

            {/* Child OKRs Count */}
            {hasChildren && (
              <div className="flex items-center space-x-1 text-gray-600">
                <RocketLaunchIcon className="h-4 w-4 text-purple-500" />
                <span className="font-medium text-gray-700">
                  {okr.child_okrs?.length || 0}
                </span>
              </div>
            )}

            {/* Relationships Count */}
            {okr.relationships && okr.relationships.length > 0 && (
              <button
                onClick={() => navigate(`/okrs/${okr.id}`)}
                className="group flex items-center space-x-1 hover:text-blue-600 transition-colors"
                title="View Relationships"
              >
                <LinkIcon className="h-4 w-4 text-blue-500" />
                <span className="font-medium text-gray-700 group-hover:text-blue-600">
                  {okr.relationships?.length || 0}
                </span>
              </button>
            )}
          </div>
        </td>
        <td className="px-4 py-3 text-right text-sm font-medium w-20">
          <div className="flex items-center justify-end space-x-2">
            {canEdit && (
              <>
                <button
                  onClick={() => navigate(`/okrs/${okr.id}`)}
                  className="text-blue-600 hover:text-blue-900 p-1"
                  title="Edit"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteOkr(okr.id)}
                  className="text-red-600 hover:text-red-900 p-1"
                  title="Delete"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </td>
      </tr>
    );
  };

  // Render OKR status badge
  const renderStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { bg: "bg-gray-100", text: "text-gray-800", label: "Draft" },
      active: { bg: "bg-green-100", text: "text-green-800", label: "Active" },
      completed: {
        bg: "bg-blue-100",
        text: "text-blue-800",
        label: "Completed",
      },
      cancelled: { bg: "bg-red-100", text: "text-red-800", label: "Cancelled" },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${config.bg} ${config.text}`}
      >
        {config.label}
      </span>
    );
  };

  // Render progress bar
  const renderProgressBar = (progress: number) => {
    const color =
      progress >= 70
        ? "bg-green-500"
        : progress >= 40
          ? "bg-yellow-500"
          : "bg-red-500";

    return (
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`${color} h-2 rounded-full transition-all duration-300`}
          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
        />
      </div>
    );
  };

  // Render empty state
  const renderEmptyState = () => {
    if (
      !planningConfig ||
      !planningConfig.cycle_types ||
      planningConfig.cycle_types.length === 0
    ) {
      return (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">
            No Planning Configuration
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Set up your planning cycles to start creating objectives and key
            results.
          </p>
          {isAdmin && (
            <div className="mt-6">
              <button
                onClick={() => navigate("/organization?tab=planning")}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                Configure Planning Cycles
              </button>
            </div>
          )}
        </div>
      );
    }

    if (okrs.length === 0) {
      return (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <RocketLaunchIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">
            No Objectives Yet
          </h3>
          <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
            Start by creating corporate objectives that define your
            organization&apos;s strategic goals. These will cascade down to team
            and individual objectives.
          </p>
          {canEdit && (
            <div className="mt-6">
              <button
                onClick={() => setShowOkrForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="mr-2 h-4 w-4" />
                Create First Objective
              </button>
            </div>
          )}
          <div className="mt-8 text-left max-w-2xl mx-auto">
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              Getting Started with OKRs:
            </h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">1.</span>
                <span>
                  Create 3-5 corporate objectives that align with your strategic
                  vision
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">2.</span>
                <span>Add 2-4 measurable key results for each objective</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">3.</span>
                <span>Cascade objectives to teams and individuals</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">4.</span>
                <span>Track progress regularly and update key results</span>
              </li>
            </ul>
          </div>
        </div>
      );
    }

    return null;
  };

  // Render OKR row
  const renderOkrRow = (okr: OrganizationOKR) => {
    const hasChildren = okr.child_okrs && okr.child_okrs.length > 0;
    const hasRelationships = okr.relationships && okr.relationships.length > 0;

    return (
      <React.Fragment key={okr.id}>
        <tr className="hover:bg-gray-50">
          <td className="px-4 py-3 text-sm w-2/5 max-w-[300px]">
            <div className="flex items-center">
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => navigate(`/okrs/${okr.id}`)}
                  className="text-left hover:text-blue-600 font-medium text-gray-900 block"
                  title={okr.objective}
                >
                  <span className="line-clamp-2">
                    {okr.parent_okr_id && (
                      <span
                        className="text-gray-400 mr-1"
                        title="Has parent objective"
                      >
                        ↳
                      </span>
                    )}
                    {okr.formatted_reference && `${okr.formatted_reference} - `}
                    {okr.objective}
                  </span>
                </button>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {/* Planning Cycle Indicator */}
                  {(() => {
                    const cycle = planningCycles.find(
                      (c) => c.id === okr.planning_cycle_id,
                    );
                    if (cycle) {
                      return (
                        <span className="inline-flex items-center text-xs text-gray-600">
                          {getCycleTypeIcon(cycle.cycle_type)} {cycle.name}
                        </span>
                      );
                    }
                    return null;
                  })()}

                  {okr.is_corporate && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                      ⭐ Corporate
                    </span>
                  )}
                </div>
                {okr.description && (
                  <p
                    className="text-xs text-gray-500 line-clamp-1 mt-1"
                    title={okr.description}
                  >
                    {okr.description}
                  </p>
                )}
              </div>
            </div>
          </td>
          <td className="px-4 py-3 text-sm text-gray-500 w-1/6">
            {okr.owner ? (
              <div className="flex items-center">
                <UserCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                <span className="truncate">
                  {okr.owner.first_name || okr.owner.last_name
                    ? `${okr.owner.first_name || ""} ${okr.owner.last_name || ""}`.trim()
                    : okr.owner.email}
                </span>
              </div>
            ) : (
              <span className="text-gray-400">Unassigned</span>
            )}
          </td>
          <td className="px-4 py-3 text-sm w-1/8">
            <span
              className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                okr.commitment_type === "aspirational"
                  ? "bg-purple-100 text-purple-800"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {formatCommitmentType(okr.commitment_type)}
            </span>
          </td>
          <td className="px-4 py-3 text-sm w-1/8">
            <OKRScoreDisplay
              progress={okr.progress_percentage || 0}
              score={okr.score}
              commitmentType={okr.commitment_type}
              showDetailed={false}
            />
          </td>
          <td className="px-4 py-3 text-sm w-1/8">
            {renderStatusBadge(okr.status)}
          </td>
          <td className="px-4 py-3 text-sm w-1/8">
            <div className="flex items-center space-x-3">
              {/* Key Results */}
              <button
                onClick={() => navigate(`/okrs/${okr.id}`)}
                className="group flex items-center space-x-1 hover:text-blue-600 transition-colors"
                title="View Key Results"
              >
                <CheckCircleIcon className="h-4 w-4 text-green-500" />
                <span className="font-medium text-gray-700 group-hover:text-blue-600">
                  {okr.key_results.length}
                </span>
              </button>

              {/* Child OKRs Count */}
              {hasChildren && (
                <div className="flex items-center space-x-1 text-gray-600">
                  <RocketLaunchIcon className="h-4 w-4 text-purple-500" />
                  <span className="font-medium text-gray-700">
                    {okr.child_okrs?.length || 0}
                  </span>
                </div>
              )}

              {/* Relationships */}
              {hasRelationships && (
                <button
                  onClick={() => navigate(`/okrs/${okr.id}`)}
                  className="group flex items-center space-x-1 hover:text-blue-600 transition-colors"
                  title="View Relationships"
                >
                  <LinkIcon className="h-4 w-4 text-blue-500" />
                  <span className="font-medium text-gray-700 group-hover:text-blue-600">
                    {okr.relationships?.length || 0}
                  </span>
                </button>
              )}
            </div>
          </td>
          <td className="px-4 py-3 text-right text-sm font-medium w-20">
            {canEdit && (
              <div className="flex items-center justify-end space-x-2">
                <button
                  onClick={() => navigate(`/okrs/${okr.id}`)}
                  className="text-blue-600 hover:text-blue-900 p-1"
                  title="Edit"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteOkr(okr.id)}
                  className="text-red-600 hover:text-red-900 p-1"
                  title="Delete"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </td>
        </tr>
      </React.Fragment>
    );
  };

  // Render OKR card for mobile view
  const renderOkrCard = (okr: OrganizationOKR) => {
    const hasChildren = okr.child_okrs && okr.child_okrs.length > 0;
    const hasRelationships = okr.relationships && okr.relationships.length > 0;

    return (
      <div
        key={okr.id}
        className="bg-white rounded-lg border border-gray-200 p-4 mb-4"
      >
        {/* Header with title and actions */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <div className="flex items-start">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 text-sm">
                  {okr.parent_okr_id && (
                    <span
                      className="text-gray-400 mr-1"
                      title="Has parent objective"
                    >
                      ↳
                    </span>
                  )}
                  {okr.formatted_reference && `${okr.formatted_reference} - `}
                  {okr.objective}
                </h3>
                {okr.is_corporate && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 mt-1">
                    Corporate
                  </span>
                )}
                {okr.description && (
                  <p className="text-xs text-gray-500 mt-1">
                    {okr.description}
                  </p>
                )}
              </div>
            </div>
          </div>
          {canEdit && (
            <div className="flex items-center space-x-2 ml-2">
              <button
                onClick={() => navigate(`/okrs/${okr.id}`)}
                className="text-blue-600 hover:text-blue-900"
                title="Edit"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDeleteOkr(okr.id)}
                className="text-red-600 hover:text-red-900"
                title="Delete"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* Owner and Status */}
        <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
          <div className="flex items-center text-gray-600">
            <UserCircleIcon className="h-4 w-4 mr-1" />
            <span className="truncate">
              {okr.owner
                ? okr.owner.first_name || okr.owner.last_name
                  ? `${okr.owner.first_name || ""} ${okr.owner.last_name || ""}`.trim()
                  : okr.owner.email
                : "Unassigned"}
            </span>
          </div>
          <div className="flex justify-end">
            {renderStatusBadge(okr.status)}
          </div>
        </div>

        {/* Type and Score */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <span
              className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                okr.commitment_type === "aspirational"
                  ? "bg-purple-100 text-purple-800"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {formatCommitmentType(okr.commitment_type)}
            </span>
          </div>
          <div className="flex justify-end">
            <OKRScoreDisplay
              progress={okr.progress_percentage || 0}
              score={okr.score}
              commitmentType={okr.commitment_type}
              showDetailed={false}
            />
          </div>
        </div>

        {/* Metrics */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center space-x-4 text-xs">
            {/* Key Results */}
            <button
              onClick={() => navigate(`/okrs/${okr.id}`)}
              className="flex items-center space-x-1 text-gray-600 hover:text-blue-600"
            >
              <CheckCircleIcon className="h-4 w-4 text-green-500" />
              <span className="font-medium">{okr.key_results.length} KRs</span>
            </button>

            {/* Child OKRs */}
            {hasChildren && (
              <div className="flex items-center space-x-1 text-gray-600">
                <RocketLaunchIcon className="h-4 w-4 text-purple-500" />
                <span className="font-medium">
                  {okr.child_okrs?.length} Child
                  {okr.child_okrs?.length === 1 ? "" : "ren"}
                </span>
              </div>
            )}

            {/* Relationships */}
            {hasRelationships && (
              <button
                onClick={() => navigate(`/okrs/${okr.id}`)}
                className="flex items-center space-x-1 text-gray-600 hover:text-blue-600"
              >
                <LinkIcon className="h-4 w-4 text-blue-500" />
                <span className="font-medium">{okr.relationships?.length}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Handle OKR deletion
  const handleDeleteOkr = async (okrId: string, force = false) => {
    if (!selectedOrg) return;

    // Initial confirmation
    if (
      !force &&
      !window.confirm("Are you sure you want to delete this objective?")
    )
      return;

    try {
      await okrService.deleteOKR(okrId, selectedOrg.id, force);
      await fetchData();
    } catch (err: any) {
      const errorDetail =
        err.response?.data?.detail || "Failed to delete objective";

      // Check if the error is about relationships and offer force deletion
      if (
        !force &&
        typeof errorDetail === "string" &&
        errorDetail.includes("relationship")
      ) {
        const forceConfirm = window.confirm(
          `${errorDetail}\n\nDo you want to force delete this OKR and remove all its relationships?`,
        );
        if (forceConfirm) {
          await handleDeleteOkr(okrId, true);
        }
      } else {
        setError(errorDetail);
      }
    }
  };

  // Handle drag end for reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setIsDragging(false);

    if (!over || active.id === over.id) return;

    const oldIndex = orderedOkrs.findIndex((okr) => okr.id === active.id);
    const newIndex = orderedOkrs.findIndex((okr) => okr.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Create new order
    const newOrder = arrayMove(orderedOkrs, oldIndex, newIndex);

    // Update order_index for all items to match their new positions
    const updatedOrder = newOrder.map((okr, index) => ({
      ...okr,
      order_index: index,
    }));

    // Update local state immediately for responsive UI
    setOkrs(updatedOrder);
    setHasChanges(true);
  };

  const handleSaveOrder = async () => {
    if (!selectedOrg || !hasChanges) return;

    try {
      // Update order_index for all OKRs
      const orderUpdates = okrs.map((okr, index) => ({
        id: String(okr.id), // Ensure it's a string
        order_index: index,
      }));

      console.log("Order updates:", orderUpdates);

      await okrService.updateOKROrder(selectedOrg.id, { items: orderUpdates });

      // Update original order to current state
      setOriginalOrder([...okrs]);
      setHasChanges(false);
      setReorderMode(false);
    } catch (err: any) {
      console.error("Error updating OKR order:", err);
      setError(err.response?.data?.detail || "Failed to update order");
      // Revert on error
      setOkrs(originalOrder);
    }
  };

  // Handle OKR creation
  const handleSaveOkr = async (
    data: OKRCreate | OKRUpdate,
    isEdit: boolean,
  ) => {
    if (!selectedOrg) return;

    try {
      // Always create new OKR (edit is now done on detail page)
      const newOkr = await okrService.createOKR(
        selectedOrg.id,
        data as OKRCreate,
      );
      setShowOkrForm(false);
      // Navigate to the new OKR's detail page
      navigate(`/okrs/${newOkr.id}`);
    } catch (err: any) {
      console.error("Error creating OKR:", err);
      setError(err.response?.data?.detail || "Failed to create objective");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              Objectives & Key Results
            </h1>
            <p className="mt-1 text-xs sm:text-sm text-gray-500">
              Manage your organization&apos;s strategic objectives and track
              progress
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {canEdit && !reorderMode && (
              <button
                onClick={() => {
                  setOriginalOrder([...okrs]);
                  setReorderMode(true);
                  setHasChanges(false);
                }}
                className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 w-full sm:w-auto"
              >
                <Bars3Icon className="mr-1 sm:mr-2 h-4 w-4" />
                Reorder
              </button>
            )}
            {canEdit && reorderMode && (
              <>
                <button
                  onClick={() => {
                    setOkrs(originalOrder);
                    setReorderMode(false);
                    setHasChanges(false);
                  }}
                  className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 w-full sm:w-auto"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveOrder}
                  disabled={!hasChanges}
                  className={`inline-flex items-center justify-center px-3 py-2 border text-xs sm:text-sm font-medium rounded-md w-full sm:w-auto ${
                    hasChanges
                      ? "border-blue-500 text-blue-700 bg-blue-50 hover:bg-blue-100"
                      : "border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed"
                  }`}
                >
                  Save Order
                  {hasChanges && (
                    <span className="ml-1 w-2 h-2 bg-blue-500 rounded-full"></span>
                  )}
                </button>
              </>
            )}
            {isAdmin && (
              <button
                onClick={() => navigate("/organization?tab=planning")}
                className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 w-full sm:w-auto"
              >
                <CalendarIcon className="mr-1 sm:mr-2 h-4 w-4" />
                Manage Cycles
              </button>
            )}
            {canEdit && (
              <button
                onClick={() => setShowOkrForm(true)}
                className="inline-flex items-center justify-center px-3 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
              >
                <PlusIcon className="mr-1 sm:mr-2 h-4 w-4" />
                New Objective
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        {/* Filters Header with Clear Button */}
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-medium text-gray-900 flex items-center">
            <FunnelIcon className="w-4 h-4 mr-1" />
            Filters
          </h3>
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="text-sm px-3 py-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-gray-200 rounded-md transition-colors flex items-center"
            >
              <XMarkIcon className="w-4 h-4 mr-1" />
              Clear All
            </button>
          )}
        </div>

        <div className="space-y-3">
          {/* Two-Row Filter Layout: Dropdowns on left, Hierarchy on right spanning both rows */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3">
            {/* Left side: Two rows of filters + search */}
            <div className="space-y-3">
              {/* Row 1: Primary dropdowns */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {/* Period Type */}
                <div className="border-l-4 border-blue-500 pl-2">
                  <MultiSelectDropdown
                    label="Period Type"
                    options={[
                      { value: "3-year", label: "3-Year" },
                      { value: "year", label: "Annual" },
                      { value: "quarter", label: "Quarterly" },
                      { value: "30-day", label: "Monthly" },
                    ].filter((opt) =>
                      planningCycles.some(
                        (cycle) => cycle.cycle_type === opt.value,
                      ),
                    )}
                    value={selectedCycleTypes}
                    onChange={(newTypes) => {
                      setSelectedCycleTypes(newTypes);
                      // Clear specific cycles when types change
                      setSelectedSpecificCycles([]);
                    }}
                    placeholder="All Types"
                  />
                </div>

                {/* Specific Period */}
                <MultiSelectDropdown
                  label="Period"
                  options={Object.entries(availableSpecificCycles).flatMap(
                    ([type, cycles]) =>
                      cycles.map((cycle) => ({
                        value: cycle.id,
                        label: cycle.name,
                      })),
                  )}
                  value={selectedSpecificCycles}
                  onChange={setSelectedSpecificCycles}
                  placeholder={
                    selectedCycleTypes.length === 0 ? "Type first" : "All"
                  }
                  disabled={selectedCycleTypes.length === 0}
                />

                {/* Parent */}
                <MultiSelectDropdown
                  label="Parent"
                  options={parentOkrs.map((okr) => ({
                    value: okr.id,
                    label: `${okr.formatted_reference ? `${okr.formatted_reference} - ` : ""}${okr.objective}`,
                  }))}
                  value={selectedParentIds}
                  onChange={setSelectedParentIds}
                  placeholder="All"
                />

                {/* Status */}
                <MultiSelectDropdown
                  label="Status"
                  options={[
                    { value: "draft", label: "Draft" },
                    { value: "active", label: "Active" },
                    { value: "completed", label: "Completed" },
                    { value: "cancelled", label: "Cancelled" },
                  ]}
                  value={statusFilters}
                  onChange={setStatusFilters}
                  placeholder="All"
                />
              </div>

              {/* Row 2: Secondary filters + Search */}
              <div className="grid grid-cols-1 md:grid-cols-[repeat(2,minmax(0,200px))_1fr] gap-2">
                {/* Type */}
                <MultiSelectDropdown
                  label="Type"
                  options={[
                    { value: "committed", label: "Committed" },
                    { value: "aspirational", label: "Aspirational" },
                  ]}
                  value={typeFilters}
                  onChange={setTypeFilters}
                  placeholder="All"
                />

                {/* Owner */}
                <MultiSelectDropdown
                  label="Owner"
                  options={[
                    { value: "unassigned", label: "Unassigned" },
                    ...uniqueOwners.map((owner) => ({
                      value: owner.id,
                      label: owner.name,
                    })),
                  ]}
                  value={selectedOwners}
                  onChange={setSelectedOwners}
                  placeholder="All"
                />

                {/* Search Bar */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Search
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="Search objectives..."
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side: Hierarchy checkboxes spanning both rows */}
            <div className="lg:self-start lg:ml-6">
              <div className="border border-gray-200 rounded-lg px-5 py-4 bg-gray-50">
                <label className="block text-xs font-medium text-gray-700 mb-3">
                  Hierarchy
                </label>
                <div className="space-y-3.5">
                  {[
                    { value: "corporate", label: "Corporate" },
                    { value: "top-level", label: "Top-Level" },
                    { value: "has-children", label: "Parents" },
                    { value: "child-only", label: "Children" },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className="flex items-center cursor-pointer hover:text-gray-900"
                    >
                      <input
                        type="checkbox"
                        checked={hierarchyFilters.includes(option.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setHierarchyFilters([
                              ...hierarchyFilters,
                              option.value,
                            ]);
                          } else {
                            setHierarchyFilters(
                              hierarchyFilters.filter((v) => v !== option.value),
                            );
                          }
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-3 text-sm text-gray-700">
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Applied Filters Bar */}
          {hasActiveFilters && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-blue-900">
                {(() => {
                  const filterCount =
                    selectedCycleTypes.length +
                    selectedSpecificCycles.length +
                    selectedOwners.length +
                    hierarchyFilters.length +
                    selectedParentIds.length +
                    (searchInput ? 1 : 0) +
                    (statusFilters.length !== 2 ||
                    !statusFilters.includes("active") ||
                    !statusFilters.includes("draft")
                      ? 1
                      : 0) +
                    typeFilters.length;
                  return `${filterCount} Active Filter${filterCount !== 1 ? "s" : ""}:`;
                })()}
              </span>

              {/* Cycle Type Filters */}
              {selectedCycleTypes.map((cycleType) => (
                <button
                  key={cycleType}
                  onClick={() =>
                    setSelectedCycleTypes(
                      selectedCycleTypes.filter((t) => t !== cycleType),
                    )
                  }
                  className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-md text-xs text-blue-900 border border-blue-300 hover:bg-blue-100"
                >
                  <span>
                    Cycle: {cycleType === "3-year" ? "3-Year" : cycleType === "year" ? "Annual" : cycleType === "quarter" ? "Quarter" : "30-Day"}
                  </span>
                  <XMarkIcon className="h-3 w-3" />
                </button>
              ))}

              {/* Specific Cycle Filters */}
              {selectedSpecificCycles.map((cycleId) => {
                const cycle = planningCycles.find((c) => c.id === cycleId);
                return cycle ? (
                  <button
                    key={cycleId}
                    onClick={() =>
                      setSelectedSpecificCycles(
                        selectedSpecificCycles.filter((id) => id !== cycleId),
                      )
                    }
                    className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-md text-xs text-blue-900 border border-blue-300 hover:bg-blue-100"
                  >
                    <span>{cycle.name}</span>
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                ) : null;
              })}

              {/* Owner Filters */}
              {selectedOwners.map((ownerId) => {
                const owner =
                  ownerId === "unassigned"
                    ? { id: "unassigned", name: "Unassigned" }
                    : uniqueOwners.find((o) => o.id === ownerId);
                return owner ? (
                  <button
                    key={ownerId}
                    onClick={() =>
                      setSelectedOwners(
                        selectedOwners.filter((id) => id !== ownerId),
                      )
                    }
                    className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-md text-xs text-blue-900 border border-blue-300 hover:bg-blue-100"
                  >
                    <span>Owner: {owner.name}</span>
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                ) : null;
              })}

              {/* Hierarchy Filters */}
              {hierarchyFilters.map((filter) => (
                <button
                  key={filter}
                  onClick={() =>
                    setHierarchyFilters(
                      hierarchyFilters.filter((f) => f !== filter),
                    )
                  }
                  className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-md text-xs text-blue-900 border border-blue-300 hover:bg-blue-100"
                >
                  <span>
                    {filter === "top-level"
                      ? "Top-Level"
                      : filter === "has-children"
                        ? "Parents"
                        : filter === "child-only"
                          ? "Children"
                          : "Corporate"}
                  </span>
                  <XMarkIcon className="h-3 w-3" />
                </button>
              ))}

              {/* Parent Filters */}
              {selectedParentIds.map((parentId) => {
                const parent = parentOkrs.find((o) => o.id === parentId);
                return parent ? (
                  <button
                    key={parentId}
                    onClick={() =>
                      setSelectedParentIds(
                        selectedParentIds.filter((id) => id !== parentId),
                      )
                    }
                    className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-md text-xs text-blue-900 border border-blue-300 hover:bg-blue-100"
                  >
                    <span>
                      Parent: {parent.formatted_reference ? `${parent.formatted_reference} - ` : ""}
                      {parent.objective.substring(0, 30)}
                      {parent.objective.length > 30 ? "..." : ""}
                    </span>
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                ) : null;
              })}

              {/* Status Filters */}
              {(statusFilters.length !== 2 ||
                !statusFilters.includes("active") ||
                !statusFilters.includes("draft")) && (
                <button
                  onClick={() => setStatusFilters(["active", "draft"])}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-md text-xs text-blue-900 border border-blue-300 hover:bg-blue-100"
                >
                  <span>Status: {statusFilters.join(", ")}</span>
                  <XMarkIcon className="h-3 w-3" />
                </button>
              )}

              {/* Type Filters */}
              {typeFilters.map((type) => (
                <button
                  key={type}
                  onClick={() =>
                    setTypeFilters(typeFilters.filter((t) => t !== type))
                  }
                  className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-md text-xs text-blue-900 border border-blue-300 hover:bg-blue-100"
                >
                  <span>Type: {formatCommitmentType(type)}</span>
                  <XMarkIcon className="h-3 w-3" />
                </button>
              ))}

              {/* Search Filter */}
              {searchInput && (
                <button
                  onClick={() => setSearchInput("")}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-md text-xs text-blue-900 border border-blue-300 hover:bg-blue-100"
                >
                  <span>Search: &ldquo;{searchInput.substring(0, 20)}{searchInput.length > 20 ? "..." : ""}&rdquo;</span>
                  <XMarkIcon className="h-3 w-3" />
                </button>
              )}
            </div>

            {/* Clear All Button */}
            <button
              onClick={() => {
                setSelectedCycleTypes([]);
                setSelectedSpecificCycles([]);
                setSelectedOwners([]);
                setHierarchyFilters([]);
                setSelectedParentIds([]);
                setStatusFilters(["active", "draft"]);
                setTypeFilters([]);
                setSearchInput("");
              }}
              className="text-xs font-medium text-blue-700 hover:text-blue-900 underline"
            >
              Clear All
            </button>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* OKR Table or Empty State */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {okrs.length === 0 && !loading ? (
        renderEmptyState()
      ) : (
        <>
          {/* Mobile/Tablet Card View - Show cards up to lg screens */}
          <div className="block lg:hidden space-y-4">
            {reorderMode ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={() => setIsDragging(true)}
                onDragEnd={handleDragEnd}
                measuring={{
                  droppable: {
                    strategy: MeasuringStrategy.Always,
                  },
                }}
              >
                <SortableContext
                  items={orderedOkrs.map((okr) => okr.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {orderedOkrs.map((okr) => (
                    <SortableOKRCard key={okr.id} okr={okr} />
                  ))}
                </SortableContext>
              </DndContext>
            ) : (
              orderedOkrs.map((okr) => renderOkrCard(okr))
            )}
          </div>

          {/* Desktop Table View - On lg screens and above */}
          <div className="hidden lg:block bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              {reorderMode ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={() => setIsDragging(true)}
                  onDragEnd={handleDragEnd}
                  measuring={{
                    droppable: {
                      strategy: MeasuringStrategy.Always,
                    },
                  }}
                >
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5 max-w-[300px]">
                          Objective
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                          Owner
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                          Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                          Score
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                          Metrics
                        </th>
                        <th className="relative px-4 py-3 w-20">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <SortableContext
                        items={orderedOkrs.map((okr) => okr.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {orderedOkrs.map((okr) => (
                          <SortableOKRRow key={okr.id} okr={okr} />
                        ))}
                      </SortableContext>
                    </tbody>
                  </table>
                </DndContext>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5 max-w-[300px]">
                        Objective
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                        Owner
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                        Score
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                        Metrics
                      </th>
                      <th className="relative px-4 py-3 w-20">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orderedOkrs.map((okr) => renderOkrRow(okr))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      <OKRFormModal
        open={showOkrForm}
        onClose={() => {
          setShowOkrForm(false);
          setInitialParentId(null);
          setInitialRelatedKrId(null);
          setInitialKrDetails(null);
        }}
        onSave={handleSaveOkr}
        okr={null}
        planningCycles={planningCycles}
        planningConfig={planningConfig}
        parentOkrs={okrs.filter((o) => o.is_corporate || !o.parent_okr_id)}
        initialParentId={initialParentId}
        initialRelatedKrId={initialRelatedKrId}
        initialKrDetails={initialKrDetails}
        canSetCorporate={isAdmin}
        organizationId={selectedOrg?.id || ""}
        onCyclesUpdate={(updatedCycles) => setPlanningCycles(updatedCycles)}
        initialFilters={{
          cycleTypes: selectedCycleTypes,
          specificCycles: selectedSpecificCycles,
          status: statusFilters,
          owners: selectedOwners,
        }}
      />
    </div>
  );
};

export default ObjectivesV2;
